import React from "react";
import { useTranslation } from "react-i18next";
import SectionTitle from "../ui/SectionTitle";
import DiscoverFilters from "./DiscoverFilters";
import DiscoverResults from "./DiscoverResults";
import { useDiscoverySearch } from "./useDiscoverySearch";
import { usePageMeta } from "../../hooks/usePageMeta";

/**
 * Public discovery page — "who can see me, and when".
 *
 * The search itself lives in `useDiscoverySearch` and the result grid in
 * `DiscoverResults`, both shared with the signed-in `Book` tab: the two surfaces
 * are the same search wearing different chrome, and a business that shows up on
 * one has to show up on the other.
 *
 * This page's own job is the public framing — the marketing-weight heading and
 * the page padding under the site header — plus the one behaviour that is
 * genuinely public: a chosen slot leaves for the business's own booking page,
 * because an anonymous visitor has no dashboard to open a modal in.
 */
const Discover = () => {
  const { t } = useTranslation();
  const search = useDiscoverySearch();
  usePageMeta({
    title: `${t("Discover.title")} | EasyPlanner`,
    description: t("Discover.description"),
  });

  return (
    <main id="discover" className="discover min-h-screen bg-ink-50 pb-20 pt-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionTitle
          title={t("Discover.title")}
          description={t("Discover.description")}
        />

        <div className="mt-10">
          <DiscoverFilters
            values={search.values}
            options={search.options}
            resultCount={search.results.total}
            onChange={search.handleFiltersChange}
            onReset={search.reset}
          />
        </div>

        <div className="mt-8">
          <DiscoverResults search={search} />
        </div>
      </div>
    </main>
  );
};

export default Discover;
